import { useState, useEffect } from "react";
import {
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  User,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import {
  profissionalService,
  especialidadeService,
} from "../../services/apiService";
import { useNotification } from "../../hooks/useNotification";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";
import PageContainer from "../../components/PageContainer";
import { ToggleSwitch } from "../../components/ui";
import { formatarCnpj } from "../../utils/FormatarDocumento";

const Profissional = () => {
  const [profissionais, setProfissionais] = useState([]);
  const [especialidades, setEspecialidades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [tipoDocumento, setTipoDocumento] = useState("cpf");
  const [paginacao, setPaginacao] = useState({
    paginaAtual: 1,
    itensPorPagina: 6,
  });
  const [formData, setFormData] = useState({
    nome: "",
    cpfCnpj: "",
    especialidade: [],
    ativo: true,
  });

  const { showSuccess, showError } = useNotification();
  const confirmDialog = useConfirmDialog();

  useEffect(() => {
    loadProfissionais();
    loadEspecialidades();
  }, []);

  const loadProfissionais = async (nome = "") => {
    setLoading(true);
    try {
      const data = await profissionalService.listarProfissionais(nome);
      setProfissionais(data);
      setPaginacao((prev) => ({ ...prev, paginaAtual: 1 }));
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    const timeoutId = setTimeout(() => {
      loadProfissionais(value.trim());
    }, 300);

    return () => clearTimeout(timeoutId);
  };

  const loadEspecialidades = async () => {
    try {
      const data = await especialidadeService.listarEspecialidades();
      setEspecialidades(data);
    } catch (err) {}
  };

  const totalRegistros = profissionais.length;
  const totalPaginas = Math.ceil(totalRegistros / paginacao.itensPorPagina);
  const indiceInicial = (paginacao.paginaAtual - 1) * paginacao.itensPorPagina;
  const indiceFinal = indiceInicial + paginacao.itensPorPagina;
  const profissionaisPaginados = profissionais.slice(
    indiceInicial,
    indiceFinal
  );

  const irParaPrimeiraPagina = () => {
    setPaginacao((prev) => ({ ...prev, paginaAtual: 1 }));
  };

  const irParaUltimaPagina = () => {
    setPaginacao((prev) => ({ ...prev, paginaAtual: totalPaginas }));
  };

  const irParaPaginaAnterior = () => {
    setPaginacao((prev) => ({
      ...prev,
      paginaAtual: Math.max(1, prev.paginaAtual - 1),
    }));
  };

  const irParaProximaPagina = () => {
    setPaginacao((prev) => ({
      ...prev,
      paginaAtual: Math.min(totalPaginas, prev.paginaAtual + 1),
    }));
  };

  const irParaPagina = (numeroPagina) => {
    setPaginacao((prev) => ({ ...prev, paginaAtual: numeroPagina }));
  };

  const gerarNumerosPaginas = () => {
    const paginas = [];
    const maxPaginasVisiveis = 5;
    let inicio = Math.max(
      1,
      paginacao.paginaAtual - Math.floor(maxPaginasVisiveis / 2)
    );
    let fim = Math.min(totalPaginas, inicio + maxPaginasVisiveis - 1);

    if (fim - inicio < maxPaginasVisiveis - 1) {
      inicio = Math.max(1, fim - maxPaginasVisiveis + 1);
    }

    for (let i = inicio; i <= fim; i++) {
      paginas.push(i);
    }

    return paginas;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleEspecialidadeChange = (especialidadeId) => {
    setFormData((prev) => {
      const currentIds = prev.especialidade.map((esp) => esp.id || esp);
      const isSelected = currentIds.includes(especialidadeId);

      if (isSelected) {
        return {
          ...prev,
          especialidade: prev.especialidade.filter(
            (esp) => (esp.id || esp) !== especialidadeId
          ),
        };
      } else {
        const especialidadeCompleta = especialidades.find(
          (esp) => esp.id === especialidadeId
        );
        return {
          ...prev,
          especialidade: [...prev.especialidade, especialidadeCompleta],
        };
      }
    });
  };

  const formatCPF = (value) => {
    if (!value) return "";
    const cleanValue = value.replace(/\D/g, "");
    return cleanValue
      .slice(0, 11)
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})/, "$1-$2");
  };

  const handleTipoDocumentoChange = (tipo) => {
    setTipoDocumento(tipo);
    setFormData((prev) => ({
      ...prev,
      cpfCnpj: "",
    }));
  };

  const handleDocumentoChange = (e) => {
    const value = e.target.value;
    // Remove tudo que não é número antes de formatar
    const cleanValue = value.replace(/\D/g, "");
    const formatted =
      tipoDocumento === "cpf" ? formatCPF(cleanValue) : formatarCnpj(cleanValue);
    setFormData((prev) => ({
      ...prev,
      cpfCnpj: formatted,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.nome.trim()) {
      showError("Nome é obrigatório");
      return;
    }

    if (!formData.cpfCnpj.trim()) {
      showError(`${tipoDocumento.toUpperCase()} é obrigatório`);
      return;
    }

    // SEMPRE limpa o documento antes de validar e enviar
    const documentoLimpo = formData.cpfCnpj.replace(/\D/g, "");
    
    const tamanhoEsperado = tipoDocumento === "cpf" ? 11 : 14;
    
    if (documentoLimpo.length !== tamanhoEsperado) {
      showError(`${tipoDocumento.toUpperCase()} inválido. Digite ${tamanhoEsperado} dígitos.`);
      return;
    }

    if (formData.especialidade.length === 0) {
      showError("Selecione pelo menos uma especialidade");
      return;
    }

    setLoading(true);

    try {
      const dataToSend = {
        nome: formData.nome,
        cpfCnpj: documentoLimpo, // Envia SOMENTE números
        especialidade: formData.especialidade,
        ativo: formData.ativo,
      };

      if (editingItem) {
        await profissionalService.atualizarProfissional(
          editingItem.id,
          dataToSend
        );
        showSuccess("Profissional atualizado com sucesso!");
      } else {
        await profissionalService.cadastrarProfissional(dataToSend);
        showSuccess("Profissional cadastrado com sucesso!");
      }

      await loadProfissionais(searchTerm);
      handleCancel();
    } catch (err) {
      showError(err.message || "Erro ao salvar profissional");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    const cleanDoc = item.cpfCnpj.replace(/\D/g, "");
    const tipo = cleanDoc.length === 11 ? "cpf" : "cnpj";
    setTipoDocumento(tipo);
    setFormData({
      nome: item.nome,
      cpfCnpj: tipo === "cpf" ? formatCPF(cleanDoc) : formatarCnpj(cleanDoc),
      especialidade: item.especialidade || [],
      ativo: item.ativo,
    });
    setShowForm(true);
};

  const handleDelete = async (id) => {
    const confirmed = await confirmDialog({
      title: "Confirmar",
      description: "Tem certeza que deseja excluir este profissional?",
    });

    if (!confirmed) {
      return;
    }

    setLoading(true);

    try {
      await profissionalService.deletarProfissional(id);
      showSuccess("Profissional excluído com sucesso!");
      await loadProfissionais(searchTerm);
    } catch (err) {
      showError(err.message || "Erro ao excluir profissional");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingItem(null);
    setTipoDocumento("cpf");
    setFormData({ nome: "", cpfCnpj: "", especialidade: [], ativo: true });
  };

  const handleNew = () => {
    setEditingItem(null);
    setTipoDocumento("cpf");
    setFormData({ nome: "", cpfCnpj: "", especialidade: [], ativo: true });
    setShowForm(true);
  };

  const formatarDocumento = (valor) => {
    if (!valor) return "";
    const cleanValue = valor.replace(/\D/g, "");
    if (cleanValue.length === 11) {
      return formatCPF(cleanValue);
    } else if (cleanValue.length === 14) {
      return formatarCnpj(cleanValue);
    }
    return valor;
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && e.target.name !== "especialidades") {
      handleSubmit();
    }
  };

  const isEspecialidadeSelected = (especialidadeId) => {
    return formData.especialidade.some(
      (esp) => (esp.id || esp) === especialidadeId
    );
  };

  return (
    <PageContainer className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Profissionais</h1>
        <p className="text-gray-600">
          Gerencie os profissionais e suas especialidades
        </p>
      </div>

      <div className="mb-6 flex flex-col sm:flex-row gap-4 justify-between">
        <div className="relative flex-1 max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Pesquisar por nome..."
            value={searchTerm}
            onChange={handleSearch}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <button
          onClick={handleNew}
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
        >
          <Plus size={20} />
          Novo Profissional
        </button>
      </div>

      {showForm && (
        <div className="mb-6 p-6 bg-gray-50 rounded-lg border">
          <h3 className="text-lg font-semibold mb-4">
            Profissional
          </h3>

          <div className="mb-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
              <label
                htmlFor="nome"
                className="block text-sm font-medium text-gray-700"
              >
                Nome *
              </label>
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-gray-700">Tipo de Documento *</span>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="tipoDocumento"
                    value="cpf"
                    checked={tipoDocumento === "cpf"}
                    onChange={() => handleTipoDocumentoChange("cpf")}
                    className="mr-2"
                  />
                  <span className="text-sm">CPF</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="tipoDocumento"
                    value="cnpj"
                    checked={tipoDocumento === "cnpj"}
                    onChange={() => handleTipoDocumentoChange("cnpj")}
                    className="mr-2"
                  />
                  <span className="text-sm">CNPJ</span>
                </label>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <input
                type="text"
                id="nome"
                name="nome"
                value={formData.nome}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Digite o nome completo"
                maxLength={100}
              />
              <input
                type="text"
                id="cpfCnpj"
                name="cpfCnpj"
                value={formData.cpfCnpj}
                onChange={handleDocumentoChange}
                onKeyDown={handleKeyDown}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={tipoDocumento === "cpf" ? "000.000.000-00" : "00.000.000/0000-00"}
                maxLength={tipoDocumento === "cpf" ? 14 : 18}
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Especialidades *
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 p-4 border border-gray-300 rounded-md bg-white max-h-40 overflow-y-auto">
              {especialidades.map((especialidade) => (
                <label
                  key={especialidade.id}
                  className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
                >
                  <input
                    type="checkbox"
                    checked={isEspecialidadeSelected(especialidade.id)}
                    onChange={() => handleEspecialidadeChange(especialidade.id)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">
                    {especialidade.nome}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="mb-4">
            <ToggleSwitch
              name="ativo"
              checked={formData.ativo}
              onChange={handleChange}
              label="Profissional ativo"
              aria-label="Profissional ativo"
            />
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleSubmit}
              disabled={
                loading ||
                !formData.nome.trim() ||
                !formData.cpfCnpj.trim() ||
                formData.especialidade.length === 0
              }
              className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Save size={16} />
              {loading ? "Salvando..." : "Salvar"}
            </button>

            <button
              onClick={handleCancel}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <X size={16} />
              Cancelar
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow border">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-semibold">Lista de Profissionais</h3>
          <span className="text-sm text-gray-500">
            {totalRegistros} registro(s) encontrado(s)
          </span>
        </div>

        {loading && profissionais.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Carregando profissionais...
          </div>
        ) : profissionais.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {searchTerm
              ? "Nenhum profissional encontrado"
              : "Nenhum profissional cadastrado"}
          </div>
        ) : (
          <>
            <div>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Profissional
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      CPF/CNPJ
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Especialidades
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {profissionaisPaginados.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8">
                            <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                              <User className="h-4 w-4 text-blue-600" />
                            </div>
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">
                              {item.nome}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatarDocumento(item.cpfCnpj)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {item.especialidade &&
                          item.especialidade.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {item.especialidade.map((esp, index) => (
                                <span
                                  key={index}
                                  className="inline-flex px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                                >
                                  {esp.nome}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-400">
                              Nenhuma especialidade
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            item.ativo
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {item.ativo ? "Ativo" : "Inativo"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleEdit(item)}
                            disabled={loading}
                            className="text-blue-600 hover:text-blue-900 disabled:text-gray-400 p-1 rounded transition-colors"
                            title="Editar"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            disabled={loading}
                            className="text-red-600 hover:text-red-900 disabled:text-gray-400 p-1 rounded transition-colors"
                            title="Excluir"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPaginas > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Mostrando {indiceInicial + 1} até{" "}
                    {Math.min(indiceFinal, totalRegistros)} de {totalRegistros}{" "}
                    registros
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={irParaPrimeiraPagina}
                      disabled={paginacao.paginaAtual === 1}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Primeira página"
                    >
                      <ChevronsLeft size={16} />
                    </button>

                    <button
                      onClick={irParaPaginaAnterior}
                      disabled={paginacao.paginaAtual === 1}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Página anterior"
                    >
                      <ChevronLeft size={16} />
                    </button>

                    <div className="flex space-x-1">
                      {gerarNumerosPaginas().map((numeroPagina) => (
                        <button
                          key={numeroPagina}
                          onClick={() => irParaPagina(numeroPagina)}
                          className={`px-3 py-2 rounded-md text-sm font-medium ${
                            paginacao.paginaAtual === numeroPagina
                              ? "bg-blue-600 text-white"
                              : "bg-white text-gray-500 border border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          {numeroPagina}
                        </button>
                      ))}
                    </div>

                    <button
                      onClick={irParaProximaPagina}
                      disabled={paginacao.paginaAtual === totalPaginas}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Próxima página"
                    >
                      <ChevronRight size={16} />
                    </button>

                    <button
                      onClick={irParaUltimaPagina}
                      disabled={paginacao.paginaAtual === totalPaginas}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Última página"
                    >
                      <ChevronsRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </PageContainer>
  );
};

export default Profissional;
